import { MobileDrawer, SearchV2, SkipLink } from "components";
import { Badge, Box, Button, IconButton, Typography } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link, useLocation, useNavigate } from "react-router-dom";
import withStyles from "@mui/styles/withStyles";
import { useTranslation } from "react-i18next";
import portalSettingsManager from "utils/portalSettingsManager";
import { LOAD_SUBJECTS, RootState, useAppDispatch } from "store";
import { useSelector } from "react-redux";
import { useStyles } from "../../header/headerStyle";
import { MyLearning } from "../myLearningMenu/myLearning";
import { MyLearningMobile } from "../myLearningMenu/myLearningMobile";
import { useEffect, useState } from "react";
import { showAttendanceLink, showNotificationLink, showServiceNowLink } from "../../header/helper";
import { NavBanner } from "components/ibf-components/nav-banner/nav-banner";
import { MenuData } from "utils/types";
import { NavBannerMobile } from "components/ibf-components/nav-banner/nav-banner-mobile";
import { MenuBookOutlined, HomeOutlined } from "@mui/icons-material";
import { useLazyQuery } from "@apollo/client/react";
import { Products } from "utils/graphql/Products";
import { GET_PRODUCTS } from "utils/queries";
import { ProductType } from "utils/graphql/Global";
import _ from "lodash";
import { ErrorToastPayload, populateProductFromEvent } from "utils/helpers";
import { Basket } from "components/ibf-components/booking-journey-components/basket/basket";
import { BasketContentDialog } from "../basket-content/basket-content-dialog";
import { BasketContentMobile } from "../basket-content/basket-content-mobile";
import { GET_BASKET_DETAILS_BY_BOOKER } from "utils/graphql/bookingJourney/booking-journey-queries";
import { BasketByBooker } from "utils/graphql/bookingJourney/models";
import {
  addToBasket,
  Basket as BasketContent,
  ModificationStatus,
  ProductBasket,
} from "store/bookingJourney";

const Action = withStyles(() => ({
  root: {
    display: "flex",
    color: "black",
    alignItems: "center",
    justifyContent: "left",
    textTransform: "none",
    textAlign: "left",
    "& div": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    "& svg": {
      marginRight: "10px",
    },
  },
}))(Button);
(Action as any).displayName = "Action";

type HeaderV2Props = {
  onClick: (val: string) => void;
  isSurveyCompleted: boolean;
  setOpenNotification: (val: boolean) => void;
  newNotificationCount?: number;
  notificationCount?: number;
  isClearNotification: boolean;
  onOpenNewLearningStepSurvey: () => void;
};

export const HeaderV2 = ({
  onClick,
  isSurveyCompleted,
  setOpenNotification,
  newNotificationCount,
  notificationCount,
  isClearNotification,
  onOpenNewLearningStepSurvey,
}: HeaderV2Props): React.ReactElement => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileProfileDrawerOpen, setMobileProfileDrawerOpen] = useState(false);
  const [courseFinderDrawerOpen, setCourseFinderDrawerOpen] = useState(false);
  const [basketContentDrawerOpen, setbasketContentDrawerOpen] = useState(false);

  const [menuData, setMenuData] = useState<MenuData[]>([]);
  const [open, setOpen] = useState(false);
  const pageSize = Number(process.env.REACT_APP_IBF_PAGE_SIZE);

  const userContext = useSelector((state: RootState) => state.core.userContext?.userContext);
  const subjects = useSelector((state: RootState) => state.core.subjects);
  const products = useSelector((state: RootState) => state.bookingJourney.basket?.products)?.filter(
    (p) => p.modificationStatus !== ModificationStatus.Deleted,
  );
  const navigateHome = () => {
    if (location.pathname !== "/") {
      navigate("/");
    }
    setMobileProfileDrawerOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const showAttendance = showAttendanceLink(userContext);
  const showServiceNow = showServiceNowLink(userContext);
  const showNotification = showNotificationLink(
    userContext,
    notificationCount,
    newNotificationCount,
    isSurveyCompleted,
    isClearNotification,
  );

  const [loadSubjects, { loading: subjectsLoading, data: subjectsData, error: subjectsError }] =
    useLazyQuery<Products>(GET_PRODUCTS, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "no-cache",
    });

  const [loadEvents, { loading: eventsLoading, data: eventsData, error: eventsError }] =
    useLazyQuery<{
      getBasketDetailsByBookerForBookingJourney: BasketByBooker;
    }>(GET_BASKET_DETAILS_BY_BOOKER);

  // Handle subjects data and errors
  useEffect(() => {
    if (subjectsData) {
      dispatch({ type: LOAD_SUBJECTS, payload: subjectsData });
    }
  }, [subjectsData, dispatch]);

  useEffect(() => {
    if (subjectsError) {
      dispatch({
        type: "alert/show",
        payload: ErrorToastPayload(t("home"), t("ibf_error_message")),
      });
    }
  }, [subjectsError, dispatch, t]);

  // Handle events data and errors
  useEffect(() => {
    if (eventsData?.getBasketDetailsByBookerForBookingJourney?.events) {
      // Grouping by multiple fields using Lodash
      const groupedEvents = _.groupBy(
        eventsData.getBasketDetailsByBookerForBookingJourney.events,
        (event) => {
          return `${event.courseId}|${event.duration}|${event.eventChannel}|${event.sessionMaxCapacity}|${event.sessionMinCapacity}|${event.eventCategory}|${event.appianPriceId}`;
        },
      );

      const productBaskets: ProductBasket[] = _.map(groupedEvents, (group) =>
        populateProductFromEvent(group),
      );
      const basket: BasketContent = {
        bookingId: eventsData.getBasketDetailsByBookerForBookingJourney.id,
        bookingNo: eventsData.getBasketDetailsByBookerForBookingJourney.bookingId,
        products: productBaskets,
      };
      dispatch(addToBasket({ basket }));
    }
  }, [eventsData, dispatch]);

  useEffect(() => {
    if (eventsError) {
      dispatch({
        type: "alert/show",
        payload: ErrorToastPayload(t("home"), t("ibf_error_message")),
      });
    }
  }, [eventsError, dispatch, t]);

  const createMenuData = (subjects: Products) => {
    let groupedMenuData = [];
    if (process.env.REACT_APP_SHOW_NON_GROUPED_MENU.toString() === "true") {
      groupedMenuData = [
        { header: "", menuList: subjects.products.children.map(({ id, name }) => ({ id, name })) },
      ];
    } else {
      groupedMenuData = Object.entries(_.groupBy(subjects.products.children, "group")).reduce(
        (acc, [group, products]) => {
          if (group) {
            acc.push({
              header: group,
              menuList: products.map(({ id, name }) => ({ id, name })),
            });
          }
          return acc;
        },
        [],
      );
    }

    setMenuData(groupedMenuData);
  };

  useEffect(() => {
    if (!subjects) {
      loadSubjects({
        variables: { productType: ProductType.CHANNEL, pageSize: pageSize, pageNo: 1 },
      });
    }
  }, []);

  useEffect(() => {
    if (!products?.length && portalSettingsManager?.application?.features?.enableBookingJourney) {
      loadEvents();
    }
  }, []);

  useEffect(() => {
    if (subjects?.products?.children) createMenuData(subjects);
  }, [subjects]);

  useEffect(() => {
    dispatch({
      type: "loader/showandhide",
      payload: { show: subjectsLoading || eventsLoading, message: t("loading") },
    });
  }, [subjectsLoading, eventsLoading]);

  return (
    <Box
      className={classes.root}
      height={{ xs: "65px", sm: "104px" }}
      bgcolor={portalSettingsManager.application?.common?.ibfThemeColor || "#FFFFFF"}
    >
      <SkipLink backColor={portalSettingsManager.application?.common?.ibfThemeColor || "#FFFFFF"} />
      <Box className={classes.hamburgerIconBox}>
        <IconButton
          onClick={() => setMobileProfileDrawerOpen(true)}
          aria-label="Open main menu"
          aria-haspopup="true"
          aria-expanded={mobileProfileDrawerOpen}
        >
          <span className={classes.srOnly}>Open main menu</span>
          {userContext && !userContext.showSurvey && !isSurveyCompleted ? (
            <MenuIcon aria-hidden="true" />
          ) : (
            <Badge variant="dot" color="secondary" aria-hidden="true">
              <MenuIcon />
            </Badge>
          )}
        </IconButton>
      </Box>
      <MobileDrawer
        isOpen={mobileProfileDrawerOpen}
        onVisibilityChange={setMobileProfileDrawerOpen}
      >
        <Box>
          <Box className={classes.hamburgerMenuWelcomeMessage}>
            <Typography>
              {t("welcome_text_ibf")} - {userContext?.userDisplayName}
            </Typography>
          </Box>
          <Box className={classes.hamburgerMenuItemHeader}>{t("menu")}</Box>
          <Action fullWidth onClick={navigateHome}>
            <HomeOutlined />
            {t("home")}
          </Action>
          <Action fullWidth onClick={() => setCourseFinderDrawerOpen(true)}>
            <MenuBookOutlined />
            {t("course_finder")}
          </Action>

          <MyLearningMobile
            newNotificationCount={newNotificationCount}
            setOpenNotification={setOpenNotification}
            showAttendanceLink={showAttendance}
            showNotification={showNotification}
            showServiceNowMenu={showServiceNow}
            products={products}
            onClick={onClick}
            setMobileProfileDrawerOpen={setMobileProfileDrawerOpen}
            setbasketContentDrawerOpen={setbasketContentDrawerOpen}
            onOpenNewLearningStepSurvey={onOpenNewLearningStepSurvey}
          />
        </Box>
      </MobileDrawer>
      <NavBannerMobile
        menuList={menuData}
        courseFinderDrawerOpen={courseFinderDrawerOpen}
        setCourseFinderDrawerOpen={setCourseFinderDrawerOpen}
      />
      <BasketContentMobile
        basketContentDrawerOpen={basketContentDrawerOpen}
        setbasketContentDrawerOpen={setbasketContentDrawerOpen}
        products={products}
        setMobileProfileDrawerOpen={setMobileProfileDrawerOpen}
      />
      <Link to="/" className={classes.logoLink}>
        <Box>
          {portalSettingsManager &&
            portalSettingsManager?.header &&
            portalSettingsManager?.header?.logo &&
            portalSettingsManager?.header?.logo?.url && (
              <Box className={classes.logoBox}>
                <img
                  src={portalSettingsManager?.header?.logo?.url || ""}
                  style={{
                    width: `${portalSettingsManager?.header?.logo?.widthInPixel || `50`}px`,
                    height: `${portalSettingsManager?.header?.logo?.heightInPixel || `20`}px`,
                  }}
                  alt={t("aria_label_welcome_header")}
                />
              </Box>
            )}
        </Box>
      </Link>
      <Box>
        <SearchV2 />
      </Box>
      <Box className={classes.menuButtonBox}>
        <NavBanner
          welcomeMessage="Welcome to KPMG’s learning content portal. <br/>
          All content available here is designed to support you on your learning journey. Enter a key word in the search box above or click a learning tile to begin your journey."
          menuList={menuData}
        />
        <MyLearning
          newNotificationCount={newNotificationCount}
          setOpenNotification={setOpenNotification}
          showAttendanceLink={showAttendance}
          showNotification={showNotification}
          showServiceNowMenu={showServiceNow}
          onClick={onClick}
          onOpenNewLearningStepSurvey={onOpenNewLearningStepSurvey}
        />
      </Box>
      <Box className={classes.BlankBox}></Box>
      {portalSettingsManager?.application?.features?.enableBookingJourney && (
        <>
          <Basket
            products={products}
            isMobileView={false}
            onClick={() => {
              setOpen(true);
            }}
          ></Basket>
          <BasketContentDialog
            open={open}
            handleClose={handleClose}
            products={products}
          ></BasketContentDialog>
        </>
      )}
      <Box className={classes.welcomeText}>
        <Typography>
          {t("welcome_text_ibf")} - {userContext?.userDisplayName}
        </Typography>
      </Box>
    </Box>
  );
};
